import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Chip,
  Skeleton,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@wc/domain/authn";
import { formatDate } from "@wc/domain/dates";

import { listTeamInvites, revokeInvite } from "../index";

/**
 * @param {object} props Component props
 * @param {string} props.teamId The team ID
 * @param {string} props.organizationId The organization ID
 * @param {() => void} [props.onRevoke] Callback when an invite is revoked
 * @returns {React.ReactElement} List of team invites
 */
export default function TeamInvitesList({ teamId, organizationId, onRevoke }) {
  const { t } = useTranslation();
  const userData = useAuth();
  const [invites, setInvites] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [revokingId, setRevokingId] = React.useState(null);

  const fetchInvites = React.useCallback(async () => {
    if (!teamId || !organizationId) return;

    try {
      setIsLoading(true);
      const invitesList = await listTeamInvites(
        teamId,
        organizationId,
        userData.getCurrentUser(),
      );
      setInvites(invitesList);
    } catch (error) {
      console.error("Failed to fetch invites:", error);
    } finally {
      setIsLoading(false);
    }
  }, [teamId, organizationId, userData]);

  React.useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleRevoke = async (token) => {
    try {
      setRevokingId(token);
      await revokeInvite(token, userData.getCurrentUser());
      setInvites(invites.filter((inv) => inv.id !== token));
      onRevoke?.();
    } catch (error) {
      console.error("Failed to revoke invite:", error);
    } finally {
      setRevokingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-12 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (invites.length === 0) {
    return (
      <p className="text-default-400 py-4 text-center text-sm">
        {t("invites.noInvites")}
      </p>
    );
  }

  return (
    <Table aria-label="Active invites" removeWrapper>
      <TableHeader>
        <TableColumn>{t("invites.created")}</TableColumn>
        <TableColumn>{t("invites.expires")}</TableColumn>
        <TableColumn>{t("invites.uses")}</TableColumn>
        <TableColumn>{t("invites.status")}</TableColumn>
        <TableColumn></TableColumn>
      </TableHeader>
      <TableBody>
        {invites.map((invite) => (
          <TableRow key={invite.id}>
            <TableCell>{formatDate(invite.createdAt)}</TableCell>
            <TableCell>{formatDate(invite.expiresAt)}</TableCell>
            <TableCell>
              {invite.maxUses
                ? `${invite.useCount}/${invite.maxUses}`
                : invite.useCount}
            </TableCell>
            <TableCell>
              <Chip
                size="sm"
                color={invite.isExpired ? "danger" : "success"}
                variant="flat"
              >
                {invite.isExpired ? t("invites.expired") : t("invites.active")}
              </Chip>
            </TableCell>
            <TableCell>
              <Button
                size="sm"
                variant="light"
                color="danger"
                isLoading={revokingId === invite.id}
                onPress={() => handleRevoke(invite.id)}
              >
                {t("invites.revoke")}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
